import { MatrixClient } from 'matrix-js-sdk/src';
import React, { createContext, useContext, ReactNode, useState, useCallback, useEffect, MutableRefObject } from 'react';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import logger from 'utils/logging/faro';
import { isWidget } from 'widget/utils/UrlParams';

import { getWidgetClient } from './WidgetBootstrap';

interface WidgetClientProviderProps {
  children: ReactNode;
}

interface WidgetClientContextValue {
  matrixClient: MutableRefObject<MatrixClient | null>;
}

const WidgetClientContext = createContext<WidgetClientContextValue | undefined>(undefined);

export const WidgetClientProvider: React.FC<WidgetClientProviderProps> = ({ children }) => {
  const matrixClient = React.useRef<MatrixClient | null>(null);
  const [isClientConnected, setIsClientConnected] = useState(false);
  const [isClientLoading, setIsClientLoading] = useState(true);

  // Initialize widget API and Matrix client
  const initializeWidget = useCallback(async () => {
    if (!isWidget() || isClientConnected) return setIsClientLoading(false);

    try {
      const clientResp = await getWidgetClient();

      if (!clientResp?.client) throw new Error('Failed to get widget client');
      matrixClient.current = clientResp.client;
    } catch (e) {
      logger.error('Widget initialization timeout or error:', e);
    } finally {
      setIsClientLoading(false);
      setIsClientConnected(true);
    }
  }, [isClientConnected]);

  useEffect(() => {
    if (isWidget()) initializeWidget();
  }, [initializeWidget]);

  if (isClientLoading && isWidget()) return <LoadingScreen />;

  return <WidgetClientContext.Provider value={{ matrixClient }}>{children}</WidgetClientContext.Provider>;
};

export const useWidgetClient = (): WidgetClientContextValue => {
  const context = useContext(WidgetClientContext);
  if (!context) throw new Error('useWidgetClient must be used within a WidgetClientProvider');

  return context;
};
